import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  dispatchSheetErrorMessage,
  plateKey,
  type Desk,
  type DispatchSheetRequirement,
  type SgrPickResponse,
  type SgrVehicleItem,
  type TicketSheetSlot,
} from '@erp/shared';
import { SgrVehicleSearch } from '@/features/sgr/SgrVehicleSearch';
import { sgrPick, useSgr } from '@/features/sgr/sgr-queries';
import {
  equipmentOfOtherClientText,
  newTicketPickPlan,
  pickedEquipmentFits,
  pickFailedText,
} from '@/features/sgr/sgr-view';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Dialog, Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { equipmentListOptions } from '@/features/resources/resources-queries';
import { creatableDesks, deskDetailQuery, useOperatedClientIds } from './tickets-queries';
import { TeamMemberPicker } from '@/features/team/TeamMemberPicker';
import { useCreateTicket } from './tickets-mutations';
import { applyOpeningTemplate } from '@/lib/opening-template';
import { ClientPicker, type ClientRef } from './ClientPicker';
import { CatalogItemPicker, useDeskCatalogs } from './CatalogItemPicker';
import { errorMessage } from './ticket-format';
import { useTenantProfile } from '@/shared/tenant-profile';
import {
  PHONE_HINT_TEXT,
  SHEET_MAIN_SLOTS,
  SHEET_MORE_SLOTS,
  emptySheetDraft,
  newTicketSheet,
  newTicketSheetProblem,
  phoneHint,
  requiredSheetSlots,
  sheetLabel,
  sheetMaxLength,
  sheetTitle,
} from './ticket-sheet-view';

export function NewTicketDialog({
  desks,
  defaultDeskId,
  defaultStageId,
  onClose,
}: {
  /** a lista de mesas da API — `dispatchSheet` diz o que o disparo da mesa exige (spec 031) */
  desks: Array<Desk & { dispatchSheet?: DispatchSheetRequirement | null }>;
  defaultDeskId: number | undefined;
  defaultStageId?: number | undefined;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const create = useCreateTicket();
  const tenantProfile = useTenantProfile();
  // spec 017: o título no formato da ficha (`PLACA-CIDADE-NOME`, spec 016) só
  // faz sentido onde o recurso é o identificador extraído do atendimento
  const titleHint =
    tenantProfile.resource.source === 'ticket_identifier'
      ? `${tenantProfile.terms.resourceId}-cidade-${tenantProfile.terms.contact}`.toUpperCase()
      : `Resumo do ${tenantProfile.terms.ticket}`;

  // spec 011 → 024: mesa espelhada do TiFlux só recebe ticket de cliente
  // operado no ERP (e ele nasce fora do espelho); sem nenhum operado ela nem
  // entra na lista, e o default cai para a primeira mesa do ERP. O par mesa ×
  // cliente é conferido abaixo, antes do POST (a API recusaria com 409).
  const operated = useOperatedClientIds();
  const creatable = creatableDesks(desks, operated, null);
  const initialDeskId =
    (defaultDeskId !== undefined && creatable.some((d) => d.id === defaultDeskId)
      ? defaultDeskId
      : creatable[0]?.id) ?? '';

  const [title, setTitle] = useState('');
  const [deskId, setDeskId] = useState<number | ''>(initialDeskId);
  const [client, setClient] = useState<ClientRef | null>(null);
  const [stageId, setStageId] = useState(
    defaultStageId !== undefined ? String(defaultStageId) : ''
  );
  const [priorityId, setPriorityId] = useState('');
  const [catalogItemId, setCatalogItemId] = useState<number | null>(null);
  // a pessoa escolhida (com o nome, para o gatilho do seletor mostrar)
  const [responsible, setResponsible] = useState<{ id: number; name: string } | null>(null);
  const [equipmentId, setEquipmentId] = useState('');
  const [requestorName, setRequestorName] = useState('');
  const [requestorEmail, setRequestorEmail] = useState('');
  const [requestorPhone, setRequestorPhone] = useState('');
  // spec 031: a ficha do associado — os campos que o disparo, a OS e a busca
  // usam. Só nos perfis em que o recurso sai do atendimento; vazio vai null
  const sheetProfile = tenantProfile.resource.source === 'ticket_identifier';
  const [sheet, setSheet] = useState(emptySheetDraft);
  const [sheetMoreOpen, setSheetMoreOpen] = useState(false);
  // spec 032: a placa da ficha é a BUSCA ao vivo no SGR (só onde a integração
  // é deste tenant); escolher preenche a ficha, liga o recurso e traz o recibo
  const sgr = useSgr();
  const sgrOn = sheetProfile && sgr.available;
  const [picked, setPicked] = useState<PickedVehicle | null>(null);
  const [pickBusy, setPickBusy] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);
  /** o ponto de venda está ligado a OUTRO cliente: a tela pergunta antes de trocar */
  const [clientAsk, setClientAsk] = useState<ClientRef | null>(null);
  // Modelo de abertura da mesa (v5): a mesa pré-selecionada já pré-preenche.
  const [description, setDescription] = useState(
    () => templateOf(desks, initialDeskId) ?? ''
  );
  const [error, setError] = useState<string | null>(null);

  /**
   * Troca de mesa: aplica o modelo de abertura da mesa nova quando a descrição
   * está intacta (regra única em lib/opening-template). Texto digitado pelo
   * usuário nunca é sobrescrito.
   */
  const selectDesk = (nextDeskId: number | '') => {
    const next = applyOpeningTemplate(
      description,
      templateOf(desks, deskId),
      templateOf(desks, nextDeskId)
    );
    if (next !== null) setDescription(next);
    setDeskId(nextDeskId);
  };

  const deskQ = useQuery({
    ...deskDetailQuery(deskId === '' ? 0 : deskId),
    enabled: deskId !== '',
  });
  const stages = deskQ.data ? [...deskQ.data.stages].sort((a, b) => a.position - b.position) : [];
  const priorities = deskQ.data
    ? [...deskQ.data.priorities].sort((a, b) => a.position - b.position)
    : [];

  // Item de catálogo (opcional): só aparece quando a mesa tem catálogo.
  const deskCatalogsQ = useDeskCatalogs(deskId === '' ? undefined : deskId);
  const deskHasCatalogs = (deskCatalogsQ.data?.length ?? 0) > 0;

  const equipmentsQ = useQuery({
    ...equipmentListOptions({
      client_ids: client ? [client.id] : undefined,
      active: true,
      limit: 200,
    }),
    enabled: client !== null,
  });
  const equipments = equipmentsQ.data?.items ?? [];

  const deskIsMirrored = desks.find((d) => d.id === deskId)?.origin === 'tiflux';
  // spec 031 (D5): a mesa manda WhatsApp ⇒ o telefone (e o nome e a placa que
  // o modelo usa) são obrigatórios — o MESMO juiz da API, antes do envio
  const selectedDesk = desks.find((d) => d.id === deskId) ?? null;
  const requirement = selectedDesk?.dispatchSheet ?? null;
  const required = requiredSheetSlots(requirement);
  const clientNotOperated = deskIsMirrored && client !== null && !operated.has(client.id);
  // o veículo escolhido serve ao cliente do ticket? (o sem associação serve a todos)
  const pickedFits = picked ? pickedEquipmentFits(picked.equipment, client?.id ?? null) : false;
  const linkedEquipment = picked && pickedFits ? picked.equipment : null;

  /** escolher um veículo da busca: lê o SGR (o servidor grava recurso e associado) e preenche */
  const pickVehicle = async (item: SgrVehicleItem) => {
    setPickError(null);
    setPickBusy(true);
    const res: SgrPickResponse = await sgrPick(item.sgrId);
    setPickBusy(false);
    if (res.status !== 'ok') {
      setPickError(pickFailedText(res, sgr.sourceName, tenantProfile.terms, true));
      return;
    }
    const plan = newTicketPickPlan(res, { sheet, title, client });
    setSheet(plan.sheet);
    setTitle(plan.title);
    setSheetMoreOpen(true);
    setClientAsk(null);
    if (plan.client.kind === 'set') setClient(plan.client.client);
    if (plan.client.kind === 'ask') setClientAsk(plan.client.client);
    setPicked({
      equipment: plan.equipment,
      receipt: plan.receipt,
      inactive: plan.inactive,
      vehicleStatus: plan.vehicleStatus,
      needsClient: plan.client.kind === 'needs_client',
      salePointName: plan.client.kind === 'needs_client' ? plan.client.salePointName : null,
    });
    setEquipmentId('');
  };

  const submit = () => {
    setError(null);
    if (!title.trim()) {
      setError('Informe o título do ticket.');
      return;
    }
    if (deskId === '') {
      setError('Selecione a mesa.');
      return;
    }
    if (!client) {
      setError('Selecione o cliente.');
      return;
    }
    if (clientNotOperated) {
      setError('Mesa espelhada do TiFlux: só recebe ticket de cliente operado no ERP. Abra este no TiFlux.');
      return;
    }
    const problem = newTicketSheetProblem({
      requirement,
      title: title.trim(),
      description: description.trim() ? description : null,
      requestorName: requestorName.trim() || null,
      requestorPhone: requestorPhone.trim() || null,
      equipmentName: linkedEquipment?.name ?? equipments.find((eq) => String(eq.id) === equipmentId)?.name ?? null,
      sheet,
    });
    if (problem) {
      setError(dispatchSheetErrorMessage(problem, selectedDesk?.displayName, tenantProfile.terms));
      return;
    }
    create.mutate(
      {
        title: title.trim(),
        description: description.trim() ? description : undefined,
        clientId: client.id,
        deskId,
        stageId: stageId === '' ? undefined : Number(stageId),
        priorityId: priorityId === '' ? undefined : Number(priorityId),
        responsibleId: responsible?.id,
        requestorName: requestorName.trim() || undefined,
        requestorEmail: requestorEmail.trim() || undefined,
        requestorPhone: requestorPhone.trim() || undefined,
        equipmentId: linkedEquipment ? linkedEquipment.id : equipmentId === '' ? undefined : Number(equipmentId),
        catalogItemId: catalogItemId ?? undefined,
        ...(sheetProfile ? { sheet: newTicketSheet(sheet) } : {}),
        // spec 032: o recibo do /pick — a foto do SGR sai DELE no servidor
        ...(picked ? { sheetFromSgr: picked.receipt } : {}),
      },
      {
        onSuccess: (ticket) => {
          onClose();
          void navigate({
            to: '/tickets/$ticketNumber',
            params: { ticketNumber: String(ticket.ticketNumber) },
          });
        },
        onError: (e) => setError(errorMessage(e)),
      }
    );
  };

  return (
    <Dialog open onClose={onClose} title="Novo ticket" className="sm:max-w-2xl">
      <form
        className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <SectionTitle>Informações do ticket</SectionTitle>

        <Field className="sm:col-span-2" label="Título" htmlFor="nt-title" required>
          <Input
            id="nt-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={titleHint}
            className="h-8"
            autoFocus
          />
        </Field>

        <Field label="Mesa" htmlFor="nt-desk" required>
          <Select
            id="nt-desk"
            className="h-8"
            value={deskId === '' ? '' : String(deskId)}
            onChange={(e) => {
              selectDesk(e.target.value ? Number(e.target.value) : '');
              setStageId('');
              setPriorityId('');
              setCatalogItemId(null);
            }}
          >
            <option value="">Selecione…</option>
            {creatable.map((d) => (
              <option key={d.id} value={d.id}>
                {d.displayName}
              </option>
            ))}
          </Select>
          {creatable.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Todas as mesas são espelhadas do TiFlux — abra o ticket no TiFlux.
            </p>
          ) : null}
        </Field>

        <Field label="Estágio" htmlFor="nt-stage">
          <Select
            id="nt-stage"
            className="h-8"
            value={stageId}
            onChange={(e) => setStageId(e.target.value)}
            disabled={deskId === '' || deskQ.isPending}
          >
            <option value="">Inicial (padrão)</option>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Prioridade" htmlFor="nt-priority">
          <Select
            id="nt-priority"
            className="h-8"
            value={priorityId}
            onChange={(e) => setPriorityId(e.target.value)}
            disabled={deskId === '' || deskQ.isPending}
          >
            <option value="">Sem prioridade</option>
            {priorities.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>

        {/* só a equipe deste tenant, com a UX do técnico (pedido da PoolTrack, 05/10) */}
        <Field label="Responsável">
          <TeamMemberPicker
            value={responsible}
            onChange={(_id, item) => setResponsible(item ? { id: item.id, name: item.name } : null)}
          />
        </Field>

        {deskId !== '' && deskHasCatalogs ? (
          <Field className="sm:col-span-2" label="Item de catálogo">
            <CatalogItemPicker
              deskId={deskId}
              value={catalogItemId}
              onChange={setCatalogItemId}
              className="grid grid-cols-1 gap-2 space-y-0 sm:grid-cols-3"
            />
          </Field>
        ) : null}

        {sheetProfile ? (
          <>
            <SectionTitle className="mt-1" data-tour="novo-ticket-ficha">
              {sheetTitle(tenantProfile.terms)}
            </SectionTitle>
            {requirement && selectedDesk ? (
              <p className="-mt-1 text-xs text-muted-foreground sm:col-span-2">
                A mesa {selectedDesk.displayName} manda WhatsApp para o {tenantProfile.terms.contact}: os
                campos com * são obrigatórios.
              </p>
            ) : null}
            {SHEET_MAIN_SLOTS.map((slot) =>
              slot === 'plate' && sgrOn ? (
                <Field
                  key={slot}
                  className="sm:col-span-2"
                  data-tour="novo-ticket-busca-origem"
                  label={`${sheetLabel(slot, tenantProfile.terms)} (busca no ${sgr.sourceName})`}
                  htmlFor="nt-sheet-plate"
                  required={required.has(slot)}
                >
                  <SgrVehicleSearch
                    id="nt-sheet-plate"
                    value={sheet.plate}
                    sourceName={sgr.sourceName}
                    terms={tenantProfile.terms}
                    placeholder={`Digite a ${tenantProfile.terms.resourceId}, o nome ou o CPF do ${tenantProfile.terms.contact}`}
                    onText={(v) => {
                      setSheet((s) => ({ ...s, plate: v }));
                      // outra placa: a escolha anterior deixa de valer (recurso e recibo)
                      if (picked && plateKey(v) !== plateKey(picked.equipment.name)) setPicked(null);
                    }}
                    onPick={(item) => void pickVehicle(item)}
                  />
                  {pickBusy ? (
                    <p className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Spinner className="size-3.5" />
                      {`Lendo no ${sgr.sourceName}…`}
                    </p>
                  ) : null}
                  {pickError ? <p className="text-xs text-amber-700 dark:text-amber-300">{pickError}</p> : null}
                  {picked ? (
                    <p className="text-xs text-muted-foreground">
                      {`Ficha preenchida do ${sgr.sourceName} — os campos continuam editáveis.`}
                      {picked.inactive ? (
                        <span className="block text-amber-700 dark:text-amber-300">
                          {`Situação no ${sgr.sourceName}: ${picked.vehicleStatus ?? '—'} — confira antes de seguir.`}
                        </span>
                      ) : null}
                    </p>
                  ) : null}
                </Field>
              ) : (
                <SheetField
                  key={slot}
                  slot={slot}
                  label={sheetLabel(slot, tenantProfile.terms)}
                  value={sheet[slot]}
                  required={required.has(slot)}
                  onChange={(v) => setSheet((s) => ({ ...s, [slot]: v }))}
                />
              )
            )}
            <Collapsible open={sheetMoreOpen} onOpenChange={setSheetMoreOpen} className="sm:col-span-2">
              <CollapsibleTrigger
                data-tour="novo-ticket-mais-dados"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                <ChevronDown
                  className={cn('size-3.5 transition-transform', sheetMoreOpen && 'rotate-180')}
                  aria-hidden
                />
                Mais dados da ficha
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                  {SHEET_MORE_SLOTS.map((slot) => (
                    <SheetField
                      key={slot}
                      slot={slot}
                      label={sheetLabel(slot, tenantProfile.terms)}
                      value={sheet[slot]}
                      onChange={(v) => setSheet((s) => ({ ...s, [slot]: v }))}
                    />
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </>
        ) : null}

        <SectionTitle className="mt-1">Cliente e solicitante</SectionTitle>

        <Field className="sm:col-span-2" label="Cliente" required>
          <ClientPicker
            selected={client}
            onSelect={(next) => {
              setClient(next);
              setEquipmentId('');
              setClientAsk(null);
            }}
          />
          {clientAsk ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs dark:border-amber-700 dark:bg-amber-950/40" role="alert">
              <p>{`No ${sgr.sourceName}, este ${tenantProfile.terms.resource} é de ${clientAsk.name}. Trocar o cliente do ${tenantProfile.terms.ticket}?`}</p>
              <div className="mt-1.5 flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setClient(clientAsk);
                    setClientAsk(null);
                  }}
                >
                  {`Trocar para ${clientAsk.name}`}
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setClientAsk(null)}>
                  Manter
                </Button>
              </div>
            </div>
          ) : null}
          {picked?.needsClient ? (
            <p className="text-xs text-muted-foreground">
              {`O ponto de venda ${picked.salePointName ?? ''} do ${sgr.sourceName} ainda não está ligado a um cliente — escolha o cliente do ${tenantProfile.terms.ticket}.`}
            </p>
          ) : null}
          {deskIsMirrored ? (
            <p className={cn('text-xs', clientNotOperated ? 'text-destructive' : 'text-muted-foreground')}>
              {clientNotOperated
                ? 'Este cliente não é operado no ERP — nesta mesa espelhada, abra o ticket no TiFlux.'
                : 'Mesa espelhada do TiFlux: só clientes operados no ERP. O ticket nasce fora do espelho.'}
            </p>
          ) : null}
        </Field>

        <Field label="Recurso" htmlFor="nt-equipment">
          {picked ? (
            <div className="space-y-1">
              <p className="flex items-center gap-2 text-sm">
                <span className="font-medium">{picked.equipment.name}</span>
                <span className="text-xs text-muted-foreground">{`(do ${sgr.sourceName})`}</span>
                <button
                  type="button"
                  className="text-xs font-medium text-primary hover:underline"
                  onClick={() => setPicked(null)}
                >
                  remover
                </button>
              </p>
              {!pickedFits && client ? (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  {`${equipmentOfOtherClientText(picked.equipment.clientName, client.name, tenantProfile.terms, sgr.sourceName)} O ${tenantProfile.terms.resource} não será ligado.`}
                </p>
              ) : null}
            </div>
          ) : (
          <Select
            id="nt-equipment"
            className="h-8"
            value={equipmentId}
            onChange={(e) => setEquipmentId(e.target.value)}
            disabled={!client || equipmentsQ.isPending}
          >
            <option value="">Sem recurso</option>
            {equipments.map((eq) => (
              <option key={eq.id} value={eq.id}>
                {eq.name}
              </option>
            ))}
          </Select>
          )}
        </Field>

        <Field label="Solicitante" htmlFor="nt-req-name">
          <Input
            id="nt-req-name"
            className="h-8"
            value={requestorName}
            onChange={(e) => setRequestorName(e.target.value)}
            placeholder="Nome"
          />
        </Field>

        <Field label="E-mail" htmlFor="nt-req-email">
          <Input
            id="nt-req-email"
            type="email"
            className="h-8"
            value={requestorEmail}
            onChange={(e) => setRequestorEmail(e.target.value)}
            placeholder="email@exemplo.com"
          />
        </Field>

        <Field label="Telefone" htmlFor="nt-req-phone">
          <Input
            id="nt-req-phone"
            className="h-8"
            value={requestorPhone}
            onChange={(e) => setRequestorPhone(e.target.value)}
            placeholder="(00) 00000-0000"
          />
        </Field>

        <SectionTitle className="mt-1">Descrição</SectionTitle>

        <Field className="sm:col-span-2" label="Detalhes do atendimento" htmlFor="nt-description">
          <Textarea
            id="nt-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Descreva o atendimento…"
            className="min-h-24"
          />
        </Field>

        {error ? (
          <p className="text-sm text-destructive sm:col-span-2" role="alert">
            {error}
          </p>
        ) : null}

        <div className="mt-1 flex justify-end gap-2 border-t pt-3 sm:col-span-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={create.isPending}>
            {create.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
            Criar ticket
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Um campo da ficha (spec 031). O telefone ganha a dica da MESMA régua do
 * disparo — "formato aceito pelo disparo" (com o aviso de fixo) ou "fora do formato (DDD + número)" —,
 * sem bloquear: o telefone é aceito como foi digitado.
 */
function SheetField({
  slot,
  label,
  value,
  required = false,
  onChange,
}: {
  slot: TicketSheetSlot;
  label: string;
  value: string;
  required?: boolean;
  onChange: (value: string) => void;
}) {
  const id = `nt-sheet-${slot}`;
  const hint = slot === 'phone' || slot === 'phoneAlt' ? phoneHint(value) : null;
  return (
    <Field label={label} htmlFor={id} required={required}>
      <Input
        id={id}
        className="h-8"
        value={value}
        maxLength={sheetMaxLength(slot)}
        onChange={(e) => onChange(e.target.value)}
        placeholder={slot === 'phone' || slot === 'phoneAlt' ? '(00) 00000-0000' : undefined}
      />
      {hint ? (
        <p className={cn('text-xs', hint === 'ok' ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-300')}>
          {PHONE_HINT_TEXT[hint]}
        </p>
      ) : null}
    </Field>
  );
}

/** o veículo escolhido na busca do SGR (spec 032) */
interface PickedVehicle {
  equipment: Extract<SgrPickResponse, { status: 'ok' }>['equipment'];
  receipt: string;
  inactive: boolean;
  vehicleStatus: string | null;
  needsClient: boolean;
  salePointName: string | null;
}

/** openingTemplate da mesa `id` na lista (null sem mesa/sem modelo). */
function templateOf(desks: Desk[], id: number | ''): string | null {
  return id === '' ? null : (desks.find((d) => d.id === id)?.openingTemplate ?? null);
}

/** `data-tour`: âncora do tour das Novidades (spec 036, D4) */
function SectionTitle({
  children,
  className,
  'data-tour': tour,
}: {
  children: ReactNode;
  className?: string;
  'data-tour'?: string;
}) {
  return (
    <div
      data-tour={tour}
      className={cn(
        'border-b pb-1.5 text-[13px] font-semibold text-foreground sm:col-span-2',
        className
      )}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  htmlFor,
  required,
  className,
  children,
  'data-tour': tour,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
  /** âncora do tour das Novidades (spec 036, D4) */
  'data-tour'?: string;
}) {
  return (
    <div data-tour={tour} className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={htmlFor} className="text-[13px] font-semibold">
        {label}
        {required ? <span className="-ml-1 text-destructive">*</span> : null}
      </Label>
      {children}
    </div>
  );
}
